import React, {
  useCallback, useEffect, useState
} from 'react';
import queryString from 'query-string';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useRequestPaging } from '@app/hooks/useRequestPaging';
import { analyticsService } from '@common/analytics';
import { getAdvisorsStatuses, getSearchList } from '@common/api/api';
import { ANALYTICS_EVENTS } from '@common/analytics/AnalyticsEvents';
import { refreshReducersFromResponse } from '@app/config/api_functions';
import classes from './classes.module.scss';
import AppLayout from '../shared/content';
import FiltersContainer from './inner_views/filters';
import AdvisorsList, { advisorsListThemes } from '../advisors_list';
import AffiliatePromoPopup from '../affiliate_promo_popup';
import {
  filterIsApplied,
  isPrerenderMode,
  parseAdvisorsFromAdvisorsState,
  renderCanonicalLink,
  getClickSourceForEvent
} from '../../config/util';
import {
  toCamelCase, isKA,
  createTemplateWithData
} from '../../../common/config/utils';
import {
  shouldTrackCategoryEvent, getTitleMetaTagName, getTopGradientColor,
  getPageTitleNameText, needRenderNoResults, getCanonicalUrl, appliedFilter
} from './search_results_util';
import {
  appRoutes, dynamicRoutes, dynamicRoutesKeys, specialityRoutesKeys
} from '../../config/app_routes';
import PromotionLabel from '../home_page/nested_views/promotion_label';
import Const, { promotionLabelTypeScreens } from '../../config/const';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';
import { updatePathTrailingSlashes } from '../routes/routes_helpers';
import { SvgIcon } from '../../../common/sprites';
import SearchResultFilterPopover from './filters_popover/filter_popover';
import { useFilterPopover } from './inner_views/filters/useFilterPopover';
import { addParamsToAnalitycs } from '../../actions/application';

const getTitleSectionImgClassName = () => {
  if (isKA()) return classes.categoryIconKA;
  return classes.categoryIcon;
};

const renderTitleSectionImg = ({ id, iconUrl, iconName }) => {
  if (!iconUrl) return null;
  if (String(id ?? '').includes('country_')) {
    return <img src={ iconUrl } alt={ iconName } className={ classes.categoryIconCountry } />;
  }
  return <SvgIcon id={ `${ iconName }_search_results_icon` } className={ getTitleSectionImgClassName() } />;
};

const renderFiltersSections = ({
  t, clearFilter, history, noResults, location, showPopover
}) => {
  if (needRenderNoResults({ noResults })) return null;
  return (
    <>
      {appliedFilter({ t, clearFilter, clearFilterIconId: 'clear_filters' })}
      <FiltersContainer history={ history } location={ location } showPopover={ showPopover } />
    </>
  );
};

const trackCategoryPageVisit = (routeKey) => {
  const matchCategoryPag = specialityRoutesKeys.includes(routeKey);
  const matchAllAdvisors = routeKey === appRoutes.psychics;
  if (matchCategoryPag) analyticsService.trackPageVisit('category page');
  if (matchAllAdvisors) analyticsService.trackPageVisit('all advisors');
};

const trackSpecialityPageVisit = (analyticEvents) => {
  if (analyticEvents?.category) {
    analyticsService.trackPageVisit('speciality page');
  }
};

const trackSearchPageVisit = (analyticEvents) => {
  if (analyticEvents?.search) {
    analyticsService.trackPageVisit('search results');
  }
};

const trackFavoritePageVisit = (analyticEvents) => {
  if (analyticEvents?.favoritesScreen) {
    analyticsService.trackPageVisit('favorite advisors');
  }
};

function SearchResults({
  routeSettings, userId
}) {
  const {
    name, backgroundColor, iconUrl, textColor, description, params, shortDescription, h1Title, iconName, id
  } = routeSettings;
  const history = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const requestParams = location.state?.params || params;
  const { routeKey } = useCurrentPath();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isOpen: _isOpen, payload: _payload, showPopover, dismissPopover: _dismissPopover
  } = useFilterPopover();

  const { loading, loadData } = useRequestPaging(getSearchList);

  const [searchResponse, setSearchResponse] = useState(window.searchResultsData || { advisors: [] });
  const [seenPages, setSeenPages] = useState([]);

  useEffect(() => {
    trackCategoryPageVisit(routeKey);
  }, [routeKey]);

  useEffect(() => {
    window.store.dispatch(addParamsToAnalitycs({
      [ANALYTICS_EVENTS.CATEGORY]: {
        clickSource: location.state?.clickSource,
        advisorId: location.state?.advisorId
      }
    }));
  }, [location.state]);

  useEffect(() => {
    const page = searchParams.get('page');
    if (parseInt(page, 10) <= 1) {
      searchParams.delete('page');
      setSearchParams(searchParams);
    }
  }, []);

  useEffect(() => {
    const { analyticEvents } = searchResponse;

    if (analyticEvents?.favoritesScreen) {
      trackFavoritePageVisit(analyticEvents);
    } else if (analyticEvents?.category) {
      trackSpecialityPageVisit(analyticEvents);
    } else if (analyticEvents?.search) {
      trackSearchPageVisit(analyticEvents);
    }

    if (shouldTrackCategoryEvent({ requestParams, analyticEvents, location })) {
      history(`${ location.pathname }?${ location.search }`, {
        state: { ...location.state, trackCategoryEvent: false },
        replace: true
      });
    }
  }, [searchResponse.analyticEvents]);

  const loadAdvisors = async (initial) => {
    if (initial) {
      setSearchResponse({ advisors: [] });
      setSeenPages([]);
    }
    const page = searchParams.get('page');
    const { clickSource } = getClickSourceForEvent({ location: window.location });
    const requestParamsWithClickSource = { ...requestParams, clickSource }; // need fot ai search click source
    const response = await loadData({ ...requestParamsWithClickSource, page }, initial);
    if (!response) return;
    if (isPrerenderMode()) {
      createTemplateWithData(Const.templateNames.searchResults, response);
    }

    setSearchResponse((prevState) => {
      const advisors = response.advisors.filter(advisor => !prevState.advisors.some(a => a.id === advisor.id));
      return {
        ...response,
        advisors: [...prevState.advisors, ...advisors]
      };
    });

    setSeenPages((prevSeenPages) => {
      if (prevSeenPages.includes(response.page)) return prevSeenPages;
      return [...prevSeenPages, response.page];
    });
  };

  useEffect(() => {
    if (window.currentUserId && !userId) return;

    window.scrollTo(0, 0);
    if (window.searchResultsData) {
      delete window.searchResultsData;
      const advisorsIds = searchResponse.advisors?.map(x => x.id);
      getAdvisorsStatuses(advisorsIds).then(res => {
        refreshReducersFromResponse(res);
        setSearchResponse(prev => ({ ...prev, advisors: parseAdvisorsFromAdvisorsState(prev?.advisors) }));
      });
    } else {
      loadAdvisors(true);
    }

    if (location.state?.params) { // Need to clear the params from the location state for the next SearchResults page
      history(location.pathname + location.search, {
        replace: true,
        state: { ...location.state, params: null }
      });
    }
  }, [routeKey, location.search, userId]);

  const clearFilter = () => {
    const search = toCamelCase(queryString.parse(location.search));
    const searchPage = typeof search.page !== 'undefined' && search.page;
    const param = queryString.stringify({
      ...(searchPage && searchPage > 1 ? { page: searchPage } : null)
    });
    history(`${ location.pathname }?${ param }`, {
      state: { needRefresh: true },
      replace: true
    });
  };

  const renderTitleSection = () => (
    <div data-testid="SR-section-title-container" className={ classes.pageTitleContainer }>
      <div className={ classes.pageTitleName } style={ { '--textColor': textColor } }>
        {renderTitleSectionImg({ id, iconUrl, iconName })}
        <h1 className={ classes.pageTitleNameText } style={ { '--textColor': textColor } }>
          {getPageTitleNameText({ name, t, h1Title })}
        </h1>
      </div>
      <div className={ classes.pageTitleDescription } style={ { '--textColor': textColor } }>
        {description}
      </div>
      <div className={ classes.pageShortDescription } style={ { '--textColor': textColor } }>
        {shortDescription}
      </div>
    </div>
  );

  const renderMetaRobotIfNeed = useCallback(() => {
    const match = dynamicRoutesKeys.filter(key => !dynamicRoutes[key].isSpeciality).includes(routeKey);
    if (match) return <meta name="robots" content="noindex, nofollow" />;
    return null;
  }, [routeKey]);

  return (
    <AppLayout topGradientColor={ getTopGradientColor({ backgroundColor }) }>
      <AffiliatePromoPopup />
      <Helmet>
        <title>{getTitleMetaTagName({ name, t })}</title>
        <meta property="og:title" content={ getTitleMetaTagName({ name, t }) } />
        {renderMetaRobotIfNeed()}
      </Helmet>
      {renderCanonicalLink(updatePathTrailingSlashes(getCanonicalUrl()))}
      <div className={ classes.container }>
        {renderTitleSection()}
        <PromotionLabel className={ classes.promotionLabelContainer } promotionLabelTypeScreen={ promotionLabelTypeScreens.searchResult } />
        {renderFiltersSections({
          t, clearFilter, history, noResults: searchResponse?.noResults, location, showPopover
        })}
        {searchResponse.noResults && !filterIsApplied({ withSearch: true }) && (
          <div className={ classes.noResultContainer } data-testid="SR-no-results-container">
            <img src={ searchResponse.noResults.iconUrl } alt="NoResult" className={ classes.noResultImg } />
            <div className={ classes.noResultTitle }>
              {searchResponse.noResults.title}
            </div>
            <div className={ classes.noResultSubTitle }>
              {searchResponse.noResults.subtitle}
            </div>
          </div>
        )}
        {(!searchResponse.noResults || filterIsApplied({ withSearch: true })) && (
          <AdvisorsList
            advisors={ parseAdvisorsFromAdvisorsState(searchResponse.advisors) }
            theme={ advisorsListThemes.searchResult }
            loadAdvisors={ loadAdvisors }
            loading={ loading }
            isLastPage={ searchResponse.isLastPage }
            analytics={ searchResponse.analytics }
            params={ requestParams }
            path={ location.pathname }
            lastPageNum={ searchResponse.lastPageNum }
            seenPages={ seenPages }
            page={ searchResponse.page }
          />
        )}
        <SearchResultFilterPopover
          isOpen={ _isOpen }
          payload={ _payload }
          dismissPopover={ _dismissPopover }
        />
      </div>
    </AppLayout>
  );
}

export default SearchResults;
